import { Box, PaletteColor, SxProps, Theme, Typography } from '@mui/material';
import { ReceiptStatusEnum } from '@pxtra-client-lib-legacy/gen';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ReceiptStatusIcon } from './receiptStatusIcon';

export interface Props {
  status: ReceiptStatusEnum;
  count?: number;
  hideCount?: boolean;
  sx?: SxProps<Theme>;
  onClick?: () => void;
  disabled?: boolean;
  outline?: boolean;
  dataTestId?: string;
}

const statusColorMap: Record<ReceiptStatusEnum | 'DISABLED', string> = {
  DRAFTED: 'grey.900',
  SUBMITTED: 'heaven.main',
  REJECTED: 'error.main',
  APPROVED: 'success.main',
  BILLED: 'success.main',
  ARCHIVED: 'grey.900',
  DELETION_SUBMITTED: 'grey.900',
  DISABLED: 'grey.900'
};

export const ReceiptStatusBadge: FC<Props> = ({
  status,
  count = 0,
  hideCount = false,
  sx,
  onClick,
  disabled,
  outline,
  dataTestId
}) => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        position: 'relative',
        borderRadius: '12px',
        overflow: 'hidden',
        maxWidth: '100%',
        padding: '12px',
        ...sx,
        cursor: onClick ? 'pointer' : 'default',
        opacity: disabled ? '0.5' : '1',
        outlineColor: (theme) => {
          return (
            theme.palette[
              statusColorMap[status].split('.')[0] as keyof typeof theme.palette
            ] as PaletteColor
          ).main;
        },
        outlineWidth: '1px',
        outlineStyle: outline ? 'solid' : 'none'
      }}
      onClick={onClick}
      data-testid={dataTestId}
    >
      <Box
        sx={{
          display: 'flex',
          flex: '1',
          paddingRight: '4px',
          overflow: 'hidden'
        }}
      >
        <ReceiptStatusIcon
          status={status}
          height={22}
          width={22}
          sx={{ display: 'inline-block', ...(disabled ? { color: 'grey.900' } : {}) }}
        />
        <Typography
          color={statusColorMap[disabled ? 'DISABLED' : status]}
          noWrap
          sx={{
            display: 'inline-block',
            lineHeight: '22px',
            paddingLeft: '4px',
            fontSize: '15px'
          }}
        >
          {t('receipts:STATUS', { context: status })}
        </Typography>
      </Box>
      <Typography
        color={statusColorMap[disabled ? 'DISABLED' : status]}
        variant="h3"
        sx={{
          display: hideCount ? 'none' : 'inline',
          lineHeight: '22px',
          width: '100%',
          textAlign: 'right',
          flexBasis: 'fit-content'
        }}
      >
        {count}
      </Typography>
      <Box
        sx={{
          backgroundColor: statusColorMap[disabled ? 'DISABLED' : status],
          opacity: '0.1',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0
        }}
      ></Box>
    </Box>
  );
};
